import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const PROTECTION_VARIANT_ID_FALLBACK = 51931843264652;
const PROTECTION_HANDLE = 'order-protection';
const PROTECTION_OPT_OUT_ATTRIBUTE = 'Order protection opted out';
const CART_DRAWER_SECTION_ID = 'cart-drawer-section';
const RESERVATION_SECONDS = 5 * 60;

let protectionMutationInFlight = null;

function getProtectionVariantId() {
  const toggle = document.querySelector('[data-protection-variant-id]');
  return Number(toggle?.getAttribute('data-protection-variant-id')) || PROTECTION_VARIANT_ID_FALLBACK;
}

function cartUrl(path) {
  const root = window.Shopify?.routes?.root || '/';
  return `${root}${path}`.replace(/([^:]\/)\/+/g, '$1');
}

async function fetchCart() {
  const response = await fetch(cartUrl('cart.js'), {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error(`Unable to load the cart (${response.status})`);
  }

  return response.json();
}

function isProtectionItem(item) {
  return (
    Number(item.variant_id) === getProtectionVariantId() ||
    item.handle === PROTECTION_HANDLE ||
    item.product_handle === PROTECTION_HANDLE
  );
}

function cartHasMerchandise(cart) {
  return Array.isArray(cart.items) && cart.items.some((item) => !isProtectionItem(item));
}

function cartHasProtection(cart) {
  return Array.isArray(cart.items) && cart.items.some(isProtectionItem);
}

function getProtectionItem(cart) {
  return Array.isArray(cart.items) ? cart.items.find(isProtectionItem) : null;
}

function protectionOptedOut(cart) {
  return String(cart.attributes?.[PROTECTION_OPT_OUT_ATTRIBUTE] || '').toLowerCase() === 'true';
}

function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('open')) {
    drawer.open();
  }
}

function updateCountMirrors(itemCount) {
  document.querySelectorAll('[data-cart-count-mirror]').forEach((mirror) => {
    mirror.textContent = itemCount < 100 ? String(itemCount) : '99+';
    mirror.classList.toggle('hidden', itemCount === 0);
  });
}

async function postCart(path, payload) {
  const response = await fetch(cartUrl(path), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  if (!response.ok || result.errors || result.status >= 400) {
    const message =
      typeof result.errors === 'string'
        ? result.errors
        : result.description || result.message || 'Unable to update shipping protection';
    throw new Error(message);
  }

  return result;
}

function dispatchCartUpdate({ action, quantity, mutation }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const protectionVariantId = getProtectionVariantId();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: [
        {
          id: String(protectionVariantId),
          quantity,
        },
      ],
      promise: deferred.promise,
    })
  );

  mutation
    .then((cart) => {
      updateCountMirrors(Number(cart.item_count) || 0);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections,
          items: cart.items,
          itemCount: Number(cart.item_count) || 0,
          source: 'avida-shipping-protection',
          didError: false,
        },
      });
    })
    .catch((error) => deferred.reject(error));

  return mutation;
}

async function requestProtectionChange(
  enabled,
  { forceOpen = false, rememberOptOut = false, clearOptOut = false } = {}
) {
  if (protectionMutationInFlight) {
    await protectionMutationInFlight;
  }

  const mutation = (async () => {
    const cart = await fetchCart();
    const protectionVariantId = getProtectionVariantId();
    const protectionItem = getProtectionItem(cart);
    const hasProtection = Boolean(protectionItem);
    const currentOptOut = protectionOptedOut(cart);
    const nextOptOut = rememberOptOut ? true : clearOptOut ? false : currentOptOut;
    const needsLineUpdate = hasProtection !== enabled;
    const needsAttributeUpdate = currentOptOut !== nextOptOut;

    if (!needsLineUpdate && !needsAttributeUpdate) {
      if (forceOpen) openCartDrawer();
      return cart;
    }

    const sectionPayload = {
      sections: [CART_DRAWER_SECTION_ID],
      sections_url: window.location.pathname,
    };
    let updatedCart = cart;
    let renderedSections;

    if (needsLineUpdate && enabled) {
      const addResult = await postCart('cart/add.js', {
        items: [{ id: protectionVariantId, quantity: 1 }],
        ...sectionPayload,
      });
      renderedSections = addResult.sections;
      updatedCart = await fetchCart();
    }

    if (needsLineUpdate && !enabled && protectionItem) {
      updatedCart = await postCart('cart/change.js', {
        id: protectionItem.key,
        quantity: 0,
        ...sectionPayload,
      });
      renderedSections = updatedCart.sections;
    }

    if (needsAttributeUpdate) {
      updatedCart = await postCart('cart/update.js', {
        attributes: {
          [PROTECTION_OPT_OUT_ATTRIBUTE]: nextOptOut ? 'true' : '',
        },
        ...sectionPayload,
      });
      renderedSections = updatedCart.sections;
    }

    if (renderedSections) {
      updatedCart.sections = renderedSections;
    }

    if (forceOpen) {
      deferredOpenCartDrawer();
    }

    return updatedCart;
  })();

  protectionMutationInFlight = dispatchCartUpdate({
    action: 'update',
    quantity: enabled ? 1 : 0,
    mutation,
  }).finally(() => {
    protectionMutationInFlight = null;
  });

  return protectionMutationInFlight;
}

function deferredOpenCartDrawer() {
  requestAnimationFrame(() => requestAnimationFrame(openCartDrawer));
}

async function reconcileProtection({ forceOpen = false, resetOptOut = false } = {}) {
  if (protectionMutationInFlight) {
    await protectionMutationInFlight;
  }

  const cart = await fetchCart();
  const hasMerchandise = cartHasMerchandise(cart);
  const hasProtection = cartHasProtection(cart);
  const optedOut = protectionOptedOut(cart);

  if (!hasMerchandise && hasProtection) {
    return requestProtectionChange(false, { clearOptOut: true });
  }

  if (hasMerchandise && !hasProtection && (resetOptOut || !optedOut)) {
    return requestProtectionChange(true, {
      forceOpen,
      clearOptOut: resetOptOut || optedOut,
    });
  }

  if (resetOptOut && optedOut) {
    return requestProtectionChange(hasProtection, {
      forceOpen,
      clearOptOut: true,
    });
  }

  return cart;
}

document.addEventListener('click', async (event) => {
  const toggle = event.target instanceof Element ? event.target.closest('[data-protection-toggle]') : null;
  if (!(toggle instanceof HTMLButtonElement)) return;

  event.preventDefault();

  const wasPressed = toggle.getAttribute('aria-pressed') === 'true';
  toggle.setAttribute('aria-pressed', String(!wasPressed));
  toggle.setAttribute('aria-busy', 'true');
  toggle.disabled = true;

  try {
    await requestProtectionChange(!wasPressed, {
      forceOpen: true,
      rememberOptOut: wasPressed,
      clearOptOut: !wasPressed,
    });
  } catch (error) {
    toggle.setAttribute('aria-pressed', String(wasPressed));
    console.error('[shipping-protection] Cart update failed:', error);
  } finally {
    if (toggle.isConnected) {
      toggle.removeAttribute('aria-busy');
      toggle.disabled = false;
    }
  }
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.target === document || !event.promise) return;

  event.promise
    .then(({ detail }) => {
      if (detail?.didError || detail?.source === 'avida-shipping-protection') return;

      return reconcileProtection({
        forceOpen: event.action === 'add',
        resetOptOut: event.action === 'add',
      });
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[shipping-protection] Automatic selection failed:', error);
      }
    });
});

class AvidaCartReservation extends HTMLElement {
  connectedCallback() {
    this.remaining = RESERVATION_SECONDS;
    this.render();
    this.timer = window.setInterval(() => {
      this.remaining = Math.max(0, this.remaining - 1);
      this.render();
      if (this.remaining === 0) window.clearInterval(this.timer);
    }, 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  render() {
    const timer = this.querySelector('[data-reservation-timer]');
    if (!timer) return;

    const minutes = Math.floor(this.remaining / 60);
    const seconds = String(this.remaining % 60).padStart(2, '0');
    timer.textContent = `${String(minutes).padStart(2, '0')}:${seconds}`;
  }
}

if (!customElements.get('avida-cart-reservation')) {
  customElements.define('avida-cart-reservation', AvidaCartReservation);
}

reconcileProtection().catch((error) => {
  console.warn('[shipping-protection] Initial selection failed:', error);
});
